import { flattenQueryParam } from '@utils/request';
import { FundriseRouteConfig } from 'vue-router';
import { getVanityUrlMetadata } from '@api/vanity';
import { useAppStore } from '@stores/app';

const modularLandingPageRoutes: FundriseRouteConfig[] = [
	{
		path: `/campaigns/income`,
		name: 'campaign-income-preset-plan',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/campaign/income-preset-plan.vue')
	},
	{
		path: `/campaigns/opportunistic-credit-fund`,
		name: 'campaign-ocf',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/opportunistic-credit-fund.vue')
	},
	{
		path: `/campaigns/opportunistic-credit-fund-ii`,
		name: 'campaign-ocf-ii',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/opportunistic-credit-fund-ii.vue')
	},
	{
		path: '/campaigns/opportunistic-credit-fund/join-waitlist',
		name: 'campaign-ocf-join-waitlist',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/opportunistic-credit-fund-join-waitlist.vue')
	},
	{
		path: '/campaigns/opportunistic-credit-fund-ii/join-waitlist',
		name: 'campaign-ocf-ii-join-waitlist',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/opportunistic-credit-fund-join-waitlist.vue')
	},
	{
		path: '/campaigns/venture',
		name: 'campaign-venture-redirect',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'vcx-landing' }
	},
	{
		path: `/campaigns/:campaignName`,
		name: 'campaign-partner-custom',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		props: true,
		component: () => import('@views/marketing-site/modular-landing-page.vue')
	},
	{
		path: `/acq-plus/start`,
		alias: [`/affiliates/start`],
		name: 'campaign-default',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		props: {
			campaignName: 'acq-plus-start',
			isAcqPlusStart: true
		},
		component: () => import('@views/marketing-site/modular-landing-page.vue')
	},
	{
		path: `/referral-program/welcome`,
		name: 'referral-program-welcome-redirect',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: (to) => {
			const inviteKey = flattenQueryParam(to.query.referralKey);

			if (inviteKey) {
				return {
					name: 'referral-welcome',
					query: {
						inviteKey
					}
				};
			} else {
				const appStore = useAppStore();
				appStore.addToast({
					type: 'error',
					message: 'The invite is not valid'
				});
				return { name: 'home' };
			}
		}
	},
	{
		path: `/invitation-program/welcome`,
		name: 'referral-welcome',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		props: {
			campaignName: 'invitation-program-welcome',
			isInvitationProgram: true
		},
		component: () => import('@views/marketing-site/modular-landing-page.vue')
	},
	{
		path: `/partner`,
		name: 'campaign-partner-default',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		props: {
			campaignName: 'partner'
		},
		component: () => import('@views/marketing-site/campaign/partner-view.vue')
	},
	{
		path: '/partner/income',
		name: 'partner-income',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		props: {
			isPartnerPage: true
		},
		component: () => import('@views/marketing-site/campaign/income-preset-plan.vue')
	},
	{
		path: '/partner/venture',
		redirect: { name: 'vcx-landing' },
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' }
	},
	{
		path: `/partner-promo`,
		name: 'campaign-partner-promo',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		props: {
			campaignName: 'partner-promo'
		},
		component: () => import('@views/marketing-site/campaign/partner-view.vue')
	},
	{
		path: `/partner/:vanityUrlSlug`,
		alias: [`/campaign/:vanityUrlSlug`],
		name: 'vanity-url-default',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: async (to, from, next) => {
			try {
				const vanityUrlMetadata = await getVanityUrlMetadata(to.params.vanityUrlSlug as string);
				next(vanityUrlMetadata.redirectUrl);
			} catch {
				next({ name: '404' });
			}
		},
		// revisit using redirect
		component: () => import('@views/marketing-site/home-view.vue')
	},
	{
		path: `/sofi`,
		name: 'sofi-redirect',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: {
			name: 'campaign-partner-custom',
			params: {
				campaignName: 'sofi'
			}
		}
	},
	{
		path: '/advisor/invite-welcome',
		name: 'advisor-invite-welcome',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		props: {
			campaignName: 'advisor-invite'
		},
		component: () => import('@views/marketing-site/modular-landing-page.vue')
	}
];

const deprecatedAcqPlusPaths = [
	'/campaigns/invest-artificial-intelligence',
	'/campaigns/invest-artificial-intelligence1'
];

const deprecatedPartnerPaths = [
	'/campaigns/partner-invest-artificial-intelligence',
	'/campaigns/partner-invest-artificial-intelligence1'
];

const acqPlusStartRedirects: FundriseRouteConfig[] = [
	{
		path: `/affiliates/no-fees`,
		name: 'campaign-nofees',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: {
			name: 'campaign-default'
		}
	},
	{
		path: '/starter-portfolio',
		name: 'starter-landing-page',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: {
			name: 'campaign-default'
		}
	},
	// Redirect all deprecatedAcqPlusPaths to '/acq-plus/start'
	...deprecatedAcqPlusPaths.map((path) => ({
		path,
		redirect: {
			name: 'campaign-default'
		}
	}))
];

const partnerRedirects: FundriseRouteConfig[] = [
	...deprecatedPartnerPaths.map((path) => ({
		path,
		redirect: {
			name: 'campaign-partner-default'
		}
	}))
];

export default [modularLandingPageRoutes, acqPlusStartRedirects, partnerRedirects].flat() as FundriseRouteConfig[];
